import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { DataService } from 'src/core/services/data.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { ModalService } from 'src/core/services/modal.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ThemeService } from 'src/core/services/thememanager.service';
import { WidgetService } from 'src/core/services/widget.service';
import { SystemIBase } from 'src/core/types/selectedSystem.types';
import { SystemHealthLastUpdated, SystemHealthOverview } from 'src/core/types/systemhealth.types';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-instrument',
  imports: [CommonModule, TranslocoModule],
  templateUrl: './instrument.component.html',
  styleUrl: './instrument.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class InstrumentComponent {
  systeminfo: SystemIBase
  moduleType: string;
  showNotif: boolean;
  olderThanSevenDays: boolean;
  theme: string = 'light';
  isLastUpdatedNull: boolean;
  hideStatusIcon: boolean = false;
  isAnyNullAndNotOlderThanSevenOrThreeDays: boolean;

  constructor(
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private widgetService: WidgetService,
    private daterangeService: DaterangeService,
    private dataservice: DataService,
    private modalService: ModalService,
    private selectedContentService: SelectedContentService,
    private deviceService: DeviceService,
    private themeService: ThemeService
  ) { }

  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  systemhealth: SystemHealthOverview;
  selectedRange;
  getDeviceType = this.deviceService.getDeviceType;
  private statusIconType: 'Error-solid' | 'Error' | 'Warning' | 'Normal' = 'Normal';
  moduleTypeValid = ['as', 'ci', 'ci 1900 analyzer', 'ci 1900 RH', 'atellica ci analyzer', 'atellica ch 930 analyzer', 'atellica im 1600 analyzer', 'atellica im 1300 analyzer', 'cc', 'ia','direct connect', 'dl','sh additional','sealer','sh connect', 'atellica decapper','sh prime'];

  get businessPartnerMode(): boolean {
    return environment.businessPartnerMode;
  }

  get canOpenAta(): boolean {
    return !this.businessPartnerMode;
  }
 

  ngOnInit(): void {
    this.themeService.currentTheme$.subscribe((theme) => {
      this.theme = theme;
    });

    this.loadingService.$isSystemHealthLoading.subscribe((isSystemLoading) => {
      this.isLoading = isSystemLoading;
    });
    this.loadingService.$isSystemHealthError.subscribe((isError) => {
      this.isError = isError;
    });
    this.loadingService.$isSystemHealthEmpty.subscribe((isEmpty) => {
      this.isEmpty = isEmpty;
    });
    this.daterangeService.$selectedRangeSystemHealth.subscribe((selectedRange) => {
      this.selectedRange = selectedRange;
      console.log("Selected date range:", this.selectedRange);
    });
    this.selectedSystemService.$systemhealth.subscribe((systemhealthdata) => {
      this.systemhealth = systemhealthdata;
    });
    this.selectedSystemService.$ibaseinformation.subscribe((ibaseinfo) => {
      this.systeminfo = ibaseinfo;
      if(this.systeminfo) {
        const moduleTypeRaw = this.systeminfo['instrument_type']?.split('-')[1];
        this.moduleType = this.toPascalCase(moduleTypeRaw);   
      }  
    });
    const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
    const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
    this.dataservice.getSystemHealthLastUpdated(material, serial).subscribe((systemData) => {
      this.evaluateLastUpdatedStatus(systemData.response);
    });
  }

  getStatusIconUrl(): string {
    const iconTheme = this.theme === 'dark' ? 'dark' : 'light';

    return `url('assets/images/Network-${this.statusIconType}_32x32_${iconTheme}.svg')`;
  }

  private isModuleTypeValidForQc(moduleType: string | null | undefined): boolean {
    if (!moduleType) {
      return false;
    }

    return this.moduleTypeValid.includes(moduleType.toLowerCase());
  }

  private getTrackedLastUpdatedDates(data: SystemHealthLastUpdated): Array<string | null | undefined> {
    const includeQc = this.isModuleTypeValidForQc(data.module_type);

    return [
      data.autocheck?.last_updated,
      data.error_events?.last_updated,
      data.maintenance?.last_updated,
      ...(includeQc ? [data.qc?.last_updated] : []),
    ];
  }

  checkIfAllNull(data: SystemHealthLastUpdated): boolean {
    const trackedDates = this.getTrackedLastUpdatedDates(data);
    return trackedDates.every((dateValue) => !dateValue);
  }

  checkIfOlderThan(data: SystemHealthLastUpdated, days: number): boolean {
    const trackedDates = this.getTrackedLastUpdatedDates(data).filter((dateValue): dateValue is string => !!dateValue);
    const threshold = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

    return trackedDates.some((lastUpdated) => {
      const parsedDate = new Date(lastUpdated);
      return !Number.isNaN(parsedDate.getTime()) && parsedDate < threshold;
    });
  }

  checkIfAnyNullAndNotOlderThanSevenOrThreeDays(data: SystemHealthLastUpdated): boolean {
    const trackedDates = this.getTrackedLastUpdatedDates(data);
    const hasAnyNull = trackedDates.some((dateValue) => !dateValue);

    if (!hasAnyNull) {
      return false;
    }

    const hasOlderThanSevenDays = this.checkIfOlderThan(data, 7);
    const hasOlderThanThreeDays = this.checkIfOlderThan(data, 3);

    return !hasOlderThanSevenDays && !hasOlderThanThreeDays;
  }

  private evaluateLastUpdatedStatus(data: SystemHealthLastUpdated): void {
    this.isLastUpdatedNull = this.checkIfAllNull(data);
    this.isAnyNullAndNotOlderThanSevenOrThreeDays = this.checkIfAnyNullAndNotOlderThanSevenOrThreeDays(data);
    this.hideStatusIcon = this.isLastUpdatedNull && !this.isModuleTypeValidForQc(data.module_type);

    if (this.hideStatusIcon) {
      this.olderThanSevenDays = false;
      this.showNotif = false;
      return;
    }

    if (this.isLastUpdatedNull || this.isAnyNullAndNotOlderThanSevenOrThreeDays) {
      this.statusIconType = 'Error-solid';
      this.olderThanSevenDays = false;
      this.showNotif = false;
      return;
    }

    this.olderThanSevenDays = this.checkIfOlderThan(data, 7);
    this.showNotif = this.checkIfOlderThan(data, 3);

    if (this.olderThanSevenDays) {
      this.statusIconType = 'Error';
      return;
    }

    this.statusIconType = this.showNotif ? 'Warning' : 'Normal';
  }

  onMaximize(filter?: string) {
    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "instrument" && !filter) {
      this.widgetService.closeWidget();
      return;
    }

    if (!filter) {
      this.widgetService.expandWidget("instrument", "Maintenance");
      return;
    }

    this.widgetService.expandWidget("instrument", filter);

  }

  checkWidgetIcon() {
    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "instrument") {
      return "minus";
    } else {
      return "plus";
    }
  }

  goToATA() {
    if (!this.canOpenAta) {
      return;
    }

    if (this.systemhealth && this.systemhealth['serial_number']) {
      const ata = this.systemhealth['serial_number'];
       // Convert to PascalCase
      let url = `https://ata.siemens-healthineers.com/notification-manager?troubleshootingType=1`;
      if(this.moduleType && this.moduleType !== '') {
        url += `&moduleType=${this.moduleType}`;
      }
      url += `&serialNo=${ata}`;
      window.open(url, '_blank');
    } else {
      console.error('ATA information is not available.');
    }
  }

  // Utility function to convert text to PascalCase
  toPascalCase(text: string): string {
    switch(text.toLowerCase()) {
      case 'atellica ch 930 analyzer':
        return 'Atellica CH 930 Analyzer';
      case 'atellica im 1300 analyzer':
      case 'atellica im 1600 analyzer':
        return 'Atellica IM 1300/1600 Analyzer';
      case 'atellica ci analyzer':
      case 'atellica ci 1900 analyzer':
        return 'Atellica CI Analyzer';
      default:
        return '';
      }
  }

  changeRange(range) {
    this.daterangeService.$selectedRangeSystemHealth.next(range);
  }

  openHelp(section) {
    this.modalService.$helpModalChanged.next({ modal: section, open: true });
  }

  openDetails() {
    this.modalService.$modalChanged.next({ modal: 'lastUpdatedDetails', open: true });
  }
}
